(ns humbleai.belt.map
  "From: https://dnaeon.github.io/recursively-merging-maps-in-clojure/")

(defn deep-merge
  "Recursively merges maps."
  [& maps]
  (letfn [(m [& xs]
            (if (some #(and (map? %) (not (record? %))) xs)
              (apply merge-with m xs)
              (last xs)))]
    (reduce m maps)))

(defn deep-merge-with
  "Recursively merges maps. Applies function f when we have duplicate keys."
  [f & maps]
  (letfn [(m [& xs]
            (if (some #(and (map? %) (not (record? %))) xs)
              (apply merge-with m xs)
              (apply f xs)))]
    (reduce m maps)))

(defn filter-entries
  [m pred]
  (into {} (filter pred) m))

(defn select-ns-keys
  "Like `select-keys`, but instead of a key-seq,
   expects something Named (keyword/symbol/string)
   as the second arg - i.e. the namespace-name to match.
   Example:

   (select-ns-keys
     {:app.foo/a 1
      :app.foo/b 2
      :app.bar/c 3}
     'app.foo)

   => #:app.foo {:a 1, :b 2}"
  [m named]
  (let [nm (name named)]
    (->> (fn [entry] (= nm (namespace (key entry))))
         (filter-entries m))))

(defn dissoc-ns-keys
  "The inverse of `select-ns-keys`. Returns a map w/o keys namespaced with <named>."
  [m named]
  (let [nm (name named)]
    (->> (fn [entry] (not= nm (namespace (key entry))))
         (filter-entries m))))

(defn select+rename-keys
  "The equivalent of the following (in one-pass):
   (-> <m>
       (select-keys <ks>)
       (set/rename-keys <smap>))

   The 2-arg arity selects *ONLY* the keys in <smap>!"
  ([m smap]
   (select+rename-keys m nil smap))
  ([m ks smap]
   (let [f (if (nil? ks)
             (fn [entry]
               (let [k (key entry)]
                 (when-some [replacement (get smap k)]
                   [replacement (val entry)])))
             (let [keyset (set ks)]
               (fn [entry]
                 (let [k (key entry)]
                   (when (contains? keyset k)
                     (if-some [replacement (get smap k)]
                       [replacement (val entry)]
                       entry))))))]
     (into {} (keep f) m))))

(defn dissoc-in
  "Analogous to `clojure.core/assoc-in`."
  [x path]
  (let [[path-1 k] ((juxt butlast last) path)]
    (update-in x path-1 dissoc k)))

(defn update-via
  "Updates matching keys between <m> & <k->fn>,
   using the corresponding 1-arg function in <k->fn>.
   If a key in <k->fn> doesn't exist in <m>, it will be
   created (per `update`), and `nil` will be passed to its
   corresponding fn to create the value."
  [m k->fn]
  (reduce-kv update m k->fn))

(defn update-in-via
  "Same as `update-via`, but expects path->fn, instead of k->fn map,
   and relies on `update-in`, rather than `update`."
  [m path->fn]
  (reduce-kv update-in m path->fn))

(defn merge!
  "An implementation of `merge` based on `assoc!`
   (i.e. expects the first arg to be a transient map)."
  [m & maps]
  (reduce (partial reduce-kv assoc!) m maps))

(defn compact
  "Shrinks a map so that it contains only 'sensical' values.
   By default (1-arg arity), the only 'nonsensical' values are identified
   via the `nil?` predicate, but the 2-arg arity allows for a custom one,
   which can be the composition of multiple others (e.g. via `every-pred`).
   The 3-arg arity controls whether the compaction should be recursive."
  ([m]
   (compact m (constantly false)))
  ([m compact-further?]
   (compact m compact-further? false))
  ([m compact-further? recur?]
   (into
    {}
    (keep
     (fn [[k v]]
       (if (and recur? (map? v) (seq v))
         [k (compact v compact-further? recur?)]
         (when-not (or (nil? v)
                       (compact-further? v)) ;; custom predicate
           [k v]))))
    m)))

(defn all-paths
  "Given a map <m>, returns a list of all its paths."
  [m]
  (when (map? m)
    (mapcat
     (fn [[k v]]
       (let [sub    (all-paths v)
             nested (map (partial apply conj [k]) (filter seq sub))]
         (if (seq nested)
           nested
           [[k]])))
     m)))

(defn leaves
  "Analogous to `clojure.core/vals`, but traverses the map until it bottoms out (1-arg arity).
   The 2-arg version expects the specific paths to pull vals from."
  ([m]
   (leaves m (all-paths m)))
  ([m paths]
   (seq (map (partial get-in m) paths))))
